import { storedModelOdd } from "./leagueStoredModels";
import { selectPrimaryTipCandidate } from "./primaryTipSelection";
import { sortFixturesCanonical } from "./fixtureOrder";
import { getFixtureScore } from "./leagueMatchdayAnalysis";
import {
  aggregateFixtureMarketOdds,
  hasConsensusOddsIndication,
  readAggregatedMarketOdd,
} from "./fixtureMarketOdds";
import { resolveFixtureLine } from "./leagueFairMarkets";

export const ADMIN_TIP_PROFILE = Object.freeze({
  threshold: 2,
  basis: "average",
  neighbourSteps: 3,
  markets: ["1x2", "asian-handicap", "over-under", "btts", "correct-score"],
});
export function fixtureTeams(f) {
  return ["home", "away"].map((side, i) => {
    const team =
      f?.participants?.find((t) => t.meta?.location === side) ||
      (i ? f?.visitorTeam : f?.localTeam) ||
      {};
    return {
      ...team,
      side,
      name:
        team.custom_name && team.custom_name !== "NO_CUSTOM_NAME"
          ? team.custom_name
          : team.name || "–",
      image: team.image_path || team.image,
    };
  });
}
export function matchdayBlocks(fixtures, rounds, selectedId) {
  const grouped = rounds.map((r) => ({
    ...r,
    fixtures: sortFixturesCanonical(fixtures.filter(
      (f) => Number(f.round_id ?? f.round?.id) === Number(r.id)
    )),
  }));
  const index = grouped.findIndex((r) => Number(r.id) === Number(selectedId));
  if (index < 0) return { previous: null, next: null };
  const complete = (round) =>
    round.fixtures.length > 0 &&
    round.fixtures.every((f) => getFixtureScore(f) !== null);
  return {
    previous: grouped.slice(0, index).reverse().find(complete) || null,
    next: grouped[index],
  };
}
export function asOracleFixture(f, league) {
  const [home, away] = fixtureTeams(f);
  return {
    id: Number(f.id),
    seasonId: f.season_id,
    leagueId: f.league_id ?? league?.id,
    kickoff: f.starting_at_timestamp ? new Date(Number(f.starting_at_timestamp) * 1000).toISOString()
      : String(f.starting_at || "").replace(" ", "T") + (/Z|[+-]\d\d:\d\d$/.test(f.starting_at || "") ? "" : "Z"),
    state: f.state,
    state_id: f.state_id,
    scores: f.scores,
    league: league?.name,
    home,
    away,
  };
}
function scoreMarkets(records) {
  const groups = {};
  for (const odd of records || []) {
    // Require an explicitly named full-time score market; never guess a market ID.
    if (
      !/^(correct score|final score|full ?time correct score)$/i.test(
        String(odd.market_description || odd.market?.name || "").trim()
      ) ||
      odd.stopped
    )
      continue;
    const match = String(odd.label || odd.name || "")
      .trim()
      .match(/^(\d+)\s*[-:]\s*(\d+)$/);
    const value = Number(odd.value);
    if (!match || !Number.isFinite(value) || value <= 1) continue;
    const key = `${Number(match[1])}:${Number(match[2])}`;
    (groups[key] ||= []).push(value);
  }
  return {
    outcomes: Object.fromEntries(
      Object.entries(groups).map(([key, values]) => [
        key,
        {
          average: values.reduce((a, b) => a + b, 0) / values.length,
          highest: Math.max(...values),
          count: values.length,
        },
      ])
    ),
  };
}
export function primaryLeagueTips(
  fixture,
  records,
  profile = ADMIN_TIP_PROFILE
) {
  if (
    !Number.isFinite(Date.parse(fixture.starting_at)) ||
    Date.parse(fixture.starting_at) <= Date.now()
  )
    return [];
  const tips = [];
  for (const market of profile.markets) {
    const candidates = [];
    const add = (outcome, fair, quotes, line = null, distance = 0) => {
      const quote = readAggregatedMarketOdd(quotes, outcome, profile.basis);
      const statistic = fixture.statistic_prediction;
      const statFair = market === "correct-score"
        ? statistic?.fair_markets?.correct_score?.[outcome]
        : storedModelOdd(statistic, market, outcome, line);
      if (hasConsensusOddsIndication(fair, statFair, quote, profile.threshold))
        candidates.push({
          fixtureId: fixture.id,
          market,
          outcome,
          fair: Number(fair),
          quote,
          advantage: (quote / Number(fair) - 1) * 100,
          line,
          distance,
        });
    };
    if (market === "1x2") {
      const quotes = aggregateFixtureMarketOdds(records, market);
      ["homewin", "draw", "awaywin"].forEach((key, i) => {
        const probability = Number(fixture.probs?.[key] ?? fixture[key]);
        if (probability > 0 && probability <= 1)
          add(["1", "X", "2"][i], 1 / probability, quotes);
      });
    } else if (market === "btts") {
      const quotes = aggregateFixtureMarketOdds(records, market);
      for (const side of ["yes", "no"])
        add(side, fixture.fair_markets?.btts?.[side], quotes);
    } else if (market === "correct-score") {
      const quotes = scoreMarkets(records);
      for (const [score, fair] of Object.entries(
        fixture.fair_markets?.correct_score || {}
      ))
        add(score, fair, quotes);
    } else {
      const source =
        fixture.fair_markets?.[
          market === "asian-handicap" ? "asian_handicaps" : "asian_goal_lines"
        ];
      if (source?.most_balanced_line == null) continue;
      const centre = resolveFixtureLine(source, "balanced:0");
      if (centre === null) continue;
      for (
        let step = -profile.neighbourSteps;
        step <= profile.neighbourSteps;
        step++
      ) {
        const line = resolveFixtureLine(source, `balanced:${step}`);
        if (line === null) continue;
        const quotes = aggregateFixtureMarketOdds(records, market, { line });
        for (const side of market === "asian-handicap"
          ? ["home", "away"]
          : ["over", "under"])
          add(side, source.lines[line]?.[side], quotes, line, Math.abs(step));
      }
    }
    const selected = selectPrimaryTipCandidate(candidates);
    if (selected) tips.push(selected);
  }
  return tips;
}

// Provider truth only: preserve stored model values and local selection.
export function mergeLeagueFixtureTruth(rows, fixtures) {
  const byId = new Map(fixtures.map(f => [Number(f.id), f]));
  return sortFixturesCanonical(rows.map(row => {
    const fresh = byId.get(Number(row.id));
    if (!fresh) return row;
    const truth = {};
    for (const key of ["state", "state_id", "scores", "starting_at", "starting_at_timestamp", "participants", "round", "round_id"])
      if (Object.prototype.hasOwnProperty.call(fresh, key)) truth[key] = fresh[key];
    return { ...row, ...truth };
  }));
}
export function leagueTipTitle(tip) {
  const [home, away] = fixtureTeams(tip.fixture);
  const team = tip.outcome === "home" || tip.outcome === "1" ? home.name : away.name;
  if (tip.market === "asian-handicap") {
    const line = Number(tip.line) * (tip.outcome === "away" ? -1 : 1);
    return `${team} ${line > 0 ? "+" : ""}${line.toLocaleString("de-DE")}`;
  }
  if (tip.market === "1x2") return tip.outcome === "X" ? "REMIS" : team;
  if (tip.market === "over-under") return `${tip.outcome === "under" ? "UNDER" : "OVER"} ${Number(tip.line).toLocaleString("de-DE", {minimumFractionDigits:1})}`;
  if (tip.market === "btts") return `BEIDE TREFFEN – ${tip.outcome === "yes" ? "JA" : "NEIN"}`;
  return tip.outcome.replace(":", "-");
}
